// src/app/lazy.tsx: code-split screens without preact/compat. Renders a spinner until the module loads (none in the page's
// first ~600ms, Feedback.tsx QuietSpinner);
// a failed load (for example a stale chunk after a deploy) is thrown to the App error boundary.
import type { ComponentType } from "preact";
import { useEffect, useState } from "preact/hooks";
import { QuietSpinner, Spinner } from "../ui/Feedback";

export { Spinner };

export function lazyScreen<P extends object>(loader: () => Promise<ComponentType<P>>): ComponentType<P> {
  let Loaded: ComponentType<P> | null = null;
  let error: unknown = null;
  let pending: Promise<void> | null = null;
  const load = () => {
    pending ??= loader().then(
      (c) => {
        Loaded = c;
      },
      (e: unknown) => {
        error = e;
      },
    );
    return pending;
  };
  function LazyScreen(props: P) {
    const [, setTick] = useState(0);
    useEffect(() => {
      if (Loaded || error) return;
      let live = true;
      void load().then(() => {
        if (live) setTick((n) => n + 1);
      });
      return () => {
        live = false;
      };
    }, []);
    if (error) throw error;
    // The page's first ~600ms show no spinner (QuietSpinner); after that, while a screen's code loads, the spinner shows at once.
    if (!Loaded) return <QuietSpinner />;
    const C = Loaded;
    return <C {...props} />;
  }
  return LazyScreen;
}
